import backend from "../../../backend/backend";
import Notify from "../../shared/Notify";
import label from "../../../i18n/label";
import BuilderActions from "./builderActions";

export const loadProjects = (intl, workspaceId) => {
  return async (dispatch) => {
    dispatch({ type: BuilderActions.LOADING_PROJECTS });

    try {
      const projects = await backend.getBuilderProjects(workspaceId);
      dispatch({
        type: BuilderActions.LOADED_PROJECTS,
        projects: projects || [],
      });
    } catch (err) {
      console.error("Failed to load builder projects", err);
      Notify.error(label(intl, "error_loading_projects", "Failed to load projects"));
      dispatch({
        type: BuilderActions.LOADED_PROJECTS,
        projects: [],
      });
    }
  };
};

export const createProject = (intl, workspaceId, data) => {
  return async (dispatch) => {
    try {
      const project = await backend.createBuilderProject(workspaceId, data);
      dispatch({
        type: BuilderActions.PROJECT_CREATED,
        project,
      });
      Notify.success(label(intl, "builder_project_created", "Project created"));
      return project;
    } catch (err) {
      console.error("Failed to create builder project", err);
      Notify.error(label(intl, "error_creating_project", "Failed to create project"));
      throw err;
    }
  };
};

export const reloadProject = (workspaceId, projectUid) => {
  return async (dispatch) => {
    try {
      const project = await backend.getBuilderProject(workspaceId, projectUid);
      console.log("[builderActionCreators] reloadProject - fetched project:", {
        uid: project?.uid,
        subdomain: project?.subdomain,
      });
      dispatch({
        type: BuilderActions.PROJECT_UPDATED,
        payload: { project },
      });
      return project;
    } catch (err) {
      console.error("Failed to reload project", err);
      throw err;
    }
  };
};

export const deleteProject = (intl, workspaceId, projectUid, onSuccess) => {
  return async (dispatch) => {
    dispatch({ type: BuilderActions.DELETING_PROJECT, uid: projectUid });

    try {
      await backend.deleteBuilderProject(workspaceId, projectUid);
      dispatch({
        type: BuilderActions.PROJECT_DELETED,
        uid: projectUid,
      });
      Notify.success(label(intl, "builder_project_deleted", "Project deleted"));
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error("Failed to delete builder project", err);
      // Clear deleting state on error
      dispatch({ type: BuilderActions.PROJECT_DELETED, uid: null });
      Notify.error(label(intl, "error_deleting_project", "Failed to delete project"));
      throw err;
    }
  };
};

export const loadBuilderChat = (intl, workspaceId, chatUid) => {
  return async (dispatch) => {
    dispatch({ type: BuilderActions.LOADING_CHAT });

    try {
      const response = await backend.getBuilderChat(workspaceId, chatUid);
      dispatch({
        type: BuilderActions.LOADED_CHAT,
        chat: response.chat,
        messages: response.messages || [],
      });

      // Check for active agent session (for auto-resume)
      try {
        const agentSession = await backend.getActiveAgentSession(workspaceId, chatUid);
        if (agentSession) {
          dispatch({
            type: BuilderActions.AGENT_SESSION_LOADED,
            session: agentSession,
          });
        }
      } catch (err) {
        // Agent session check is optional, don't fail the whole load
        console.log("No active agent session found");
      }
    } catch (err) {
      console.error("Failed to load builder chat", err);
      Notify.error(label(intl, "error_loading_chat", "Failed to load chat"));
      dispatch({
        type: BuilderActions.MESSAGE_ERROR,
        error: { message: "Failed to load chat" },
      });
    }
  };
};

export const clearBuilderChat = () => {
  return (dispatch, getState) => {
    const projectUid = getState().builder.currentProjectUid;

    dispatch({ type: BuilderActions.CLEAR_CHAT });
    dispatch({ type: BuilderActions.AGENT_CLEAR });
    dispatch({ type: BuilderActions.CURRENT_PROJECT_CLEARED });

    // Clear current project's sources state
    if (projectUid) {
      dispatch({ type: BuilderActions.SOURCES_CLEAR_PROJECT, projectUid });
    }

    // Reset current project UID
    dispatch({ type: BuilderActions.SET_CURRENT_PROJECT, projectUid: null });
  };
};

// Current project action creators (single project view)

export const loadCurrentProject = (workspaceId, projectUid) => {
  return async (dispatch) => {
    dispatch({ type: BuilderActions.CURRENT_PROJECT_LOADING });

    try {
      const project = await backend.getBuilderProject(workspaceId, projectUid);
      dispatch({
        type: BuilderActions.CURRENT_PROJECT_LOADED,
        payload: { project },
      });
      return project;
    } catch (err) {
      console.error("Failed to load current project", err);
      dispatch({
        type: BuilderActions.CURRENT_PROJECT_LOADED,
        payload: { project: null },
      });
      throw err;
    }
  };
};

export const updateCurrentProject = (project) => ({
  type: BuilderActions.CURRENT_PROJECT_UPDATED,
  payload: { project },
});

export const clearCurrentProject = () => ({
  type: BuilderActions.CURRENT_PROJECT_CLEARED,
});

// Project context action creators (UID only, for per-project state isolation)

export const setCurrentProject = (projectUid) => ({
  type: BuilderActions.SET_CURRENT_PROJECT,
  projectUid,
});

// Sources (file tree/editor) action creators

export const loadSourcesTree = (workspaceId, projectUid, path = null) => {
  return async (dispatch, getState) => {
    dispatch({ type: BuilderActions.SOURCES_TREE_LOADING, projectUid });

    try {
      const tree = await backend.getProjectFileTree(workspaceId, projectUid, path);
      dispatch({
        type: BuilderActions.SOURCES_TREE_LOADED,
        projectUid,
        tree: tree || [],
      });

      // Auto-expand first level folders if this is the initial load and expandedPaths is empty
      const projectState = getState().builder.sourcesByProject[projectUid];
      const currentExpandedPaths = projectState?.expandedPaths || [];
      if (currentExpandedPaths.length === 0 && tree && tree.length > 0) {
        const firstLevelFolders = tree
          .filter((node) => node.type === "folder")
          .map((node) => node.path);
        if (firstLevelFolders.length > 0) {
          dispatch({
            type: BuilderActions.SOURCES_PATHS_EXPANDED,
            projectUid,
            payload: firstLevelFolders,
          });
        }
      }
    } catch (err) {
      console.error("Failed to load file tree", err);
      dispatch({
        type: BuilderActions.SOURCES_TREE_LOADED,
        projectUid,
        tree: [],
      });
    }
  };
};

export const expandSourcesFolder = (workspaceId, projectUid, path) => {
  return async (dispatch) => {
    try {
      const children = await backend.getProjectFileTree(workspaceId, projectUid, path);
      dispatch({
        type: BuilderActions.SOURCES_FOLDER_EXPANDED,
        projectUid,
        path,
        children: children || [],
      });
    } catch (err) {
      console.error("Failed to expand folder", err);
    }
  };
};

export const searchSourcesFiles = (workspaceId, projectUid, query) => {
  return async (dispatch) => {
    if (!query || query.trim().length < 2) {
      dispatch({ type: BuilderActions.SOURCES_SEARCH_CLEAR, projectUid });
      return;
    }

    dispatch({
      type: BuilderActions.SOURCES_SEARCH_LOADING,
      projectUid,
      query,
    });

    try {
      const results = await backend.searchProjectFiles(workspaceId, projectUid, query);
      dispatch({
        type: BuilderActions.SOURCES_SEARCH_LOADED,
        projectUid,
        results,
      });
    } catch (err) {
      console.error("Failed to search files", err);
      dispatch({
        type: BuilderActions.SOURCES_SEARCH_LOADED,
        projectUid,
        results: { filenameMatches: [], contentMatches: [] },
      });
    }
  };
};

export const clearSourcesSearch = (projectUid) => {
  return { type: BuilderActions.SOURCES_SEARCH_CLEAR, projectUid };
};

export const openSourcesFile = (projectUid, file) => {
  return { type: BuilderActions.SOURCES_FILE_OPENED, projectUid, file };
};

export const closeSourcesFile = (projectUid, path) => {
  return { type: BuilderActions.SOURCES_FILE_CLOSED, projectUid, path };
};

export const activateSourcesTab = (projectUid, path) => {
  return { type: BuilderActions.SOURCES_TAB_ACTIVATED, projectUid, path };
};

export const loadSourcesFileContent = (workspaceId, projectUid, path) => {
  return async (dispatch) => {
    try {
      const fileContent = await backend.getProjectFileContent(workspaceId, projectUid, path);
      dispatch({
        type: BuilderActions.SOURCES_FILE_CONTENT_LOADED,
        projectUid,
        path,
        content: fileContent.content,
        readOnly: fileContent.readOnly,
      });
    } catch (err) {
      console.error("Failed to load file content", err);
      dispatch({
        type: BuilderActions.SOURCES_FILE_CONTENT_LOAD_ERROR,
        projectUid,
        path,
        error: err.message || "Failed to load file",
      });
    }
  };
};

export const changeSourcesFileContent = (projectUid, path, content) => {
  return {
    type: BuilderActions.SOURCES_FILE_CONTENT_CHANGED,
    projectUid,
    path,
    content,
  };
};

export const saveSourcesFile = (intl, workspaceId, projectUid, path, content) => {
  return async (dispatch) => {
    dispatch({ type: BuilderActions.SOURCES_FILE_SAVING, projectUid, path });

    try {
      await backend.saveProjectFileContent(workspaceId, projectUid, path, content);
      dispatch({ type: BuilderActions.SOURCES_FILE_SAVED, projectUid, path });
    } catch (err) {
      console.error("Failed to save file", err);
      dispatch({ type: BuilderActions.SOURCES_FILE_SAVE_ERROR, projectUid, path });
      Notify.error(label(intl, "error_saving_file", "Failed to save file"));
    }
  };
};

export const notifySourcesExternalChange = (projectUid, path, changeAction) => {
  return {
    type: BuilderActions.SOURCES_FILE_EXTERNAL_CHANGE,
    projectUid,
    path,
    changeAction,
  };
};

// File change notification - debounce timers stored per-project
const fileChangeDebounceTimers = {};
const FILE_CHANGE_DEBOUNCE_MS = 500;

/**
 * Handle agent file change event - marks files stale, queues for reload.
 * Called when AGENT_FILE_CHANGE WebSocket message is received.
 */
export const handleAgentFileChange = (workspaceId, projectUid, change) => {
  return (dispatch, getState) => {
    const { path, action, timestamp } = change;

    // 1. Mark file as stale if it's in open tabs
    dispatch({
      type: BuilderActions.SOURCES_MARK_FILES_STALE,
      projectUid,
      paths: [path],
    });

    // 2. Queue the change
    dispatch({
      type: BuilderActions.SOURCES_QUEUE_FILE_CHANGE,
      projectUid,
      change: { path, action, timestamp: timestamp || Date.now() },
    });

    // 3. If file was created/deleted, flag tree for refresh
    if (action === "create" || action === "delete") {
      dispatch({ type: BuilderActions.SOURCES_FLAG_TREE_REFRESH, projectUid });
    }

    // 4. Reset debounce timer
    if (fileChangeDebounceTimers[projectUid]) {
      clearTimeout(fileChangeDebounceTimers[projectUid]);
    }

    fileChangeDebounceTimers[projectUid] = setTimeout(() => {
      dispatch(flushPendingFileChanges(workspaceId, projectUid));
    }, FILE_CHANGE_DEBOUNCE_MS);
  };
};

/**
 * Flush pending file changes - batch reload stale open tabs.
 */
export const flushPendingFileChanges = (workspaceId, projectUid) => {
  return async (dispatch, getState) => {
    const projectState = getState().builder.sourcesByProject[projectUid];
    if (!projectState) return;

    const { pendingFileChanges, openTabs, needsTreeRefresh } = projectState;

    // Find which pending changes affect open tabs
    const changedPaths = new Set(pendingFileChanges.map((c) => c.path));
    const tabsToReload = openTabs.filter(
      (tab) => tab.isStale && changedPaths.has(tab.path)
    );

    // Batch fetch content for affected tabs (only non-dirty ones will auto-update)
    if (tabsToReload.length > 0) {
      dispatch({ type: BuilderActions.SOURCES_BATCH_RELOAD_START, projectUid });

      const results = await Promise.all(
        tabsToReload.map((tab) =>
          backend.getProjectFileContent(workspaceId, projectUid, tab.path)
            .then((res) => ({ path: tab.path, content: res.content, success: true }))
            .catch((err) => ({ path: tab.path, error: err.message, success: false }))
        )
      );

      dispatch({
        type: BuilderActions.SOURCES_BATCH_RELOAD_COMPLETE,
        projectUid,
        results,
      });
    }

    // Refresh file tree if needed (files created/deleted)
    if (needsTreeRefresh) {
      dispatch(loadSourcesTree(workspaceId, projectUid));
    }

    // Clear pending queue
    dispatch({ type: BuilderActions.SOURCES_CLEAR_PENDING_CHANGES, projectUid });

    // Clean up timer reference
    delete fileChangeDebounceTimers[projectUid];
  };
};

/**
 * Resolve file conflict - user chose to keep their changes.
 */
export const resolveFileConflictKeepMine = (projectUid, path) => {
  return {
    type: BuilderActions.SOURCES_CLEAR_STALE_FLAG,
    projectUid,
    path,
  };
};

/**
 * Resolve file conflict - user chose to discard and reload.
 */
export const resolveFileConflictReload = (projectUid, path) => {
  return (dispatch, getState) => {
    const projectState = getState().builder.sourcesByProject[projectUid];
    if (!projectState) return;

    const tab = projectState.openTabs.find((t) => t.path === path);
    if (!tab || !tab.pendingContent) return;

    // Update content to the pending (external) content
    dispatch({
      type: BuilderActions.SOURCES_FILE_CONTENT_LOADED,
      projectUid,
      path,
      content: tab.pendingContent,
      readOnly: tab.readOnly,
    });

    // Clear stale flag and pending content
    dispatch({
      type: BuilderActions.SOURCES_CLEAR_STALE_FLAG,
      projectUid,
      path,
    });
  };
};

export const resetSources = (projectUid) => {
  return { type: BuilderActions.SOURCES_RESET, projectUid };
};

export const toggleSourcesPath = (projectUid, path) => {
  return { type: BuilderActions.SOURCES_PATH_TOGGLED, projectUid, payload: path };
};

export const expandSourcesPaths = (projectUid, paths) => {
  return { type: BuilderActions.SOURCES_PATHS_EXPANDED, projectUid, payload: paths };
};

/**
 * Extract all parent folder paths from a file path.
 * E.g., "src/components/Button.jsx" → ["src", "src/components"]
 */
export const getParentPaths = (filePath) => {
  const parts = filePath.split("/");
  const parentPaths = [];
  for (let i = 1; i < parts.length; i++) {
    parentPaths.push(parts.slice(0, i).join("/"));
  }
  return parentPaths;
};

// Project events action creators

export const loadProjectEvents = (workspaceId, projectUid) => {
  return async (dispatch) => {
    dispatch({ type: BuilderActions.PROJECT_EVENTS_LOADING, projectUid });

    try {
      const events = await backend.getProjectEvents(workspaceId, projectUid);
      dispatch({
        type: BuilderActions.PROJECT_EVENTS_LOADED,
        projectUid,
        events: events || [],
      });
    } catch (err) {
      console.error("Failed to load project events", err);
      dispatch({
        type: BuilderActions.PROJECT_EVENTS_LOADED,
        projectUid,
        events: [],
      });
    }
  };
};

export const receiveProjectEvent = (projectUid, event) => {
  return {
    type: BuilderActions.PROJECT_EVENT_RECEIVED,
    projectUid,
    event,
  };
};

// Template action creators

export const loadTemplates = () => {
  return async (dispatch) => {
    dispatch({ type: BuilderActions.TEMPLATES_LOADING });

    try {
      const templates = await backend.getTemplates();
      dispatch({
        type: BuilderActions.TEMPLATES_LOADED,
        templates: templates || [],
      });
    } catch (err) {
      console.error("Failed to load templates", err);
      dispatch({
        type: BuilderActions.TEMPLATES_LOAD_ERROR,
        error: err.message || "Failed to load templates",
      });
    }
  };
};

export const loadTemplate = (templateUid) => {
  return async (dispatch) => {
    dispatch({ type: BuilderActions.TEMPLATE_LOADING });

    try {
      const template = await backend.getTemplate(templateUid);
      dispatch({
        type: BuilderActions.TEMPLATE_LOADED,
        template,
      });
      return template;
    } catch (err) {
      console.error("Failed to load template", err);
      dispatch({ type: BuilderActions.TEMPLATE_LOAD_ERROR });
      throw err;
    }
  };
};

const TEMPLATE_POLL_INTERVAL = 3000; // 3 seconds
const TEMPLATE_POLL_MAX_ATTEMPTS = 60; // Max 3 minutes of polling

export const exportAsTemplate = (intl, workspaceId, projectUid) => {
  return async (dispatch) => {
    dispatch({ type: BuilderActions.TEMPLATE_EXPORTING });

    try {
      const result = await backend.exportAsTemplate(workspaceId, projectUid);

      // If cloning/deployment is in progress, poll for status
      if (["CLONING", "BUILDING", "PENDING"].includes(result.deploymentStatus)) {
        const finalResult = await pollTemplateDeployment(result.uid);
        dispatch({ type: BuilderActions.TEMPLATE_EXPORTED });

        // Handle failure states (ERROR or STOPPED)
        if (finalResult.deploymentStatus === "ERROR" || finalResult.deploymentStatus === "STOPPED") {
          Notify.error(label(intl, "template_deployment_failed", "Template deployment failed"));
          return { ...finalResult, deploymentFailed: true };
        }

        return finalResult;
      }

      // Handle immediate failure (ERROR returned directly without polling)
      if (result.deploymentStatus === "ERROR" || result.deploymentStatus === "STOPPED") {
        dispatch({ type: BuilderActions.TEMPLATE_EXPORT_ERROR });
        Notify.error(label(intl, "template_deployment_failed", "Template deployment failed"));
        return { ...result, deploymentFailed: true };
      }

      dispatch({ type: BuilderActions.TEMPLATE_EXPORTED });
      return result;
    } catch (err) {
      console.error("Failed to export as template", err);
      dispatch({ type: BuilderActions.TEMPLATE_EXPORT_ERROR });
      Notify.error(label(intl, "export_template_failed", "Failed to export template"));
      throw err;
    }
  };
};

/**
 * Poll template status until deployment is complete or failed.
 * Statuses: CLONING -> BUILDING -> RUNNING/ERROR/STOPPED
 * We continue polling while status is CLONING, BUILDING, or PENDING.
 */
const pollTemplateDeployment = async (templateUid) => {
  let attempts = 0;
  const IN_PROGRESS_STATUSES = ["CLONING", "BUILDING", "PENDING"];

  while (attempts < TEMPLATE_POLL_MAX_ATTEMPTS) {
    await new Promise((resolve) => setTimeout(resolve, TEMPLATE_POLL_INTERVAL));
    attempts += 1;

    try {
      const template = await backend.getTemplate(templateUid);

      // Continue polling only if status is in progress
      if (!IN_PROGRESS_STATUSES.includes(template.deploymentStatus)) {
        return template;
      }
    } catch (err) {
      console.error("Error polling template status", err);
      // Continue polling on error, might be temporary
    }
  }

  // Timeout - return with pending status, let caller handle
  return { uid: templateUid, deploymentStatus: "TIMEOUT" };
};

export const instantiateTemplate = (intl, workspaceId, templateUid) => {
  return async (dispatch) => {
    dispatch({ type: BuilderActions.TEMPLATE_INSTANTIATING });

    try {
      const result = await backend.instantiateTemplate(workspaceId, templateUid);
      dispatch({ type: BuilderActions.TEMPLATE_INSTANTIATED });
      Notify.success(label(intl, "project_created_from_template", "Project created from template"));
      return result;
    } catch (err) {
      console.error("Failed to instantiate template", err);
      dispatch({ type: BuilderActions.TEMPLATE_INSTANTIATE_ERROR });
      Notify.error(label(intl, "instantiate_template_failed", "Failed to create project from template"));
      throw err;
    }
  };
};

export const deleteTemplate = (intl, templateUid, onSuccess) => {
  return async (dispatch) => {
    dispatch({ type: BuilderActions.DELETING_TEMPLATE, uid: templateUid });

    try {
      await backend.deleteTemplate(templateUid);
      dispatch({
        type: BuilderActions.TEMPLATE_DELETED,
        uid: templateUid,
      });
      Notify.success(label(intl, "template_deleted", "Template deleted"));
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error("Failed to delete template", err);
      dispatch({ type: BuilderActions.TEMPLATE_DELETED, uid: null });
      Notify.error(label(intl, "error_deleting_template", "Failed to delete template"));
      throw err;
    }
  };
};

export const clearTemplate = () => ({
  type: BuilderActions.TEMPLATE_CLEAR,
});
